/**
 * All user-facing strings of the hub, in German. Game modules keep their own
 * table next to their code and pass it through the ViewContext's `t()`.
 *
 * Keys are dotted paths grouped by page. Placeholders are `{name}`. Keep
 * strings short and warm; no exclamation marks in system messages
 * (docs/DESIGN.md, "Writing").
 */
export const de = {
  'app.name': 'Schulspiele',
  'app.tagline': 'Spiele für den Unterricht',

  'nav.teacher': 'Lehrerbereich',
  'nav.play': 'Mitspielen',
  'nav.guide': 'Anleitungen',
  'nav.home': 'Zur Startseite',
  'nav.logout': 'Abmelden',

  'notFound.title': 'Diesen Weg gibt es im Wald nicht.',

  'start.playHint': 'Du bist Schülerin oder Schüler und hast eine PIN?',
  'start.emptyTitle': 'Hier wächst bald ein Spiel.',
  'start.emptyText': 'Sobald das erste Spielformat fertig ist, steht es hier.',
  'start.noSets': 'Noch kein Set für dieses Spiel.',
  'start.set': 'Fragenset',
  'start.onlyDrafts': 'Es gibt nur Entwürfe. Mach eins fertig, dann kannst du es starten.',
  'start.noOptions': 'Für dieses Spiel gibt es nichts einzustellen.',
  'start.titleSuffix': 'starten',

  'format.devices': 'Mit Handys oder Tablets',
  'format.noDevices': 'Nur Tafel, keine Schülergeräte',
  'format.devicesOptional': 'Mit oder ohne Handys',
  'format.sets': 'Mit Fragensets',
  'format.bank': 'Fragen aus der Bibliothek',
  'format.media': 'Bilder, Ton und Video',
  'format.startAsTeacher': 'Als Lehrkraft starten',
  'format.notFound': 'Dieses Spielformat gibt es nicht.',

  'login.title': 'Anmelden',
  'login.username': 'Benutzername',
  'login.password': 'Passwort',
  'login.submit': 'Anmelden',
  'login.failed': 'Benutzername oder Passwort stimmt nicht.',
  'login.throttled': 'Zu viele Versuche. Bitte in {minutes} Min. noch einmal.',
  'login.busy': 'Gerade melden sich viele an. Einen Moment …',
  'login.busyLater': 'Gerade melden sich viele an. Probier es gleich noch einmal.',
  'login.noAccount': 'Noch kein Zugang? Frag die Person, die Schulspiele an deiner Schule betreut.',
  'login.demo': 'Demo: Benutzer „demo“, Passwort „demo-passwort“.',

  'teacher.hello': 'Hallo {name}',
  'teacher.games': 'Spiel starten',
  'teacher.sessions': 'Laufende Spiele',
  'teacher.noGames': 'Noch keine Spielformate freigeschaltet.',
  'teacher.hiddenGames': 'Test- und Referenzmodule',
  'teacher.start': 'Starten',
  'teacher.startNow': 'Sofort starten',
  'teacher.open': 'Öffnen',
  'teacher.noSessions': 'Gerade läuft kein Spiel.',
  'teacher.cancel': 'Abbrechen',

  'library.title': 'Meine Sets',
  'library.media': 'Medien',
  'library.mediaHint': 'Bilder, Ton und Videos für deine Fragen',
  'library.search': 'Suchen',
  'library.searchPlaceholder': 'Titel eines Fragensets',
  'library.new': 'Neues Set anlegen',
  'library.newFor': 'Neues Set: {game}',
  'library.newOne': 'Neues Set',
  'library.more': 'Mehr zu „{title}“',
  'library.empty': 'Noch keine Fragensets. Leg oben dein erstes an.',
  'library.emptyNoFormats':
    'Noch keine Fragensets. Sobald ein Format mit Fragensets da ist, legst du sie hier an.',
  'library.noMatch': 'Kein Set passt zur Suche.',
  'library.orphaned': 'Format nicht verfügbar',
  'library.updated': 'Zuletzt geändert {date}',
  'library.untitled': 'Ohne Titel',
  'library.draft': 'Entwurf: {count} offen',
  'library.fromExample': 'Aus Vorlage',
  'library.examplesTitle': 'Vorlage wählen',
  'library.examplesLead':
    'Die Vorlage wird als eigenes Set in deine Bibliothek kopiert. Du kannst sie danach frei ändern.',
  'library.useExample': 'Übernehmen',
  'library.exampleCopied': 'Vorlage übernommen.',
  'library.edit': 'Bearbeiten',
  'library.duplicate': 'Duplizieren',
  'library.duplicated': 'Kopie angelegt.',
  'library.delete': 'Löschen',
  'library.confirmDelete': '„{title}“ wird endgültig gelöscht.',
  'library.deleted': 'Gelöscht.',

  'session.pin': 'PIN',
  'session.qrLabel': 'Beitreten mit dieser PIN',
  'session.joinAt': 'Beitreten unter',
  'session.steps': 'So geht es los',
  'session.runningNote': 'Das Spiel läuft. Du lenkst es in der Steuerung.',
  'session.endedNote': 'Das Spiel ist beendet.',
  'session.gameOverNote': 'Das Spiel ist zu Ende. „Schließen“ beendet es für alle.',
  'session.step1': 'Tafel öffnen und auf dem Bildschirm im Raum anzeigen.',
  'session.step2': 'Die Kinder scannen den QR-Code oder tippen die PIN ein.',
  'session.step3': 'Alle da? Spiel starten und die Steuerung aufs Handy holen.',
  'session.step3NoDevices': 'Spiel starten und über die Steuerung (Handy oder Laptop) lenken.',
  'session.openBoard': 'Tafel öffnen',
  'session.openControl': 'Steuerung öffnen',
  'session.details': 'PIN und Lobby',
  'session.boardLink': 'Tafel-Link',
  'session.back': 'Zurück',
  'session.start': 'Spiel starten',
  'password.change': 'Passwort ändern',
  'password.title': 'Passwort ändern',
  'password.old': 'Bisheriges Passwort',
  'password.new': 'Neues Passwort',
  'password.repeat': 'Neues Passwort noch einmal',
  'password.hint': 'Mindestens 10 Zeichen. Auf anderen Geräten musst du dich danach neu anmelden.',
  'password.save': 'Ändern',
  'password.saved': 'Passwort geändert.',
  'password.needOld': 'Gib dein bisheriges Passwort ein.',
  'password.tooShort': 'Das neue Passwort braucht mindestens 10 Zeichen.',
  'password.mismatch': 'Die beiden neuen Passwörter sind nicht gleich.',
  'session.end': 'Beenden',
  'session.close': 'Schließen',
  'session.ended': 'Beendet',
  'session.endedToast': 'Spiel beendet.',
  'session.lobby': 'Lobby',
  'session.running': 'Läuft',
  'session.gameOverPill': 'Zu Ende',
  'session.participants': 'Dabei: {count}',
  'session.nobodyYet': 'Noch niemand da.',
  'session.offline': 'Verbindung unterbrochen',
  'session.lock': 'Beitritt sperren',
  'session.unlock': 'Beitritt öffnen',
  'session.kick': 'Entfernen',
  'session.cancel': 'Abbrechen',
  'session.kickHint':
    'Ein entferntes Kind kann mit der PIN neu beitreten. Soll es draußen bleiben: „Beitritt sperren“.',
  'session.kickOffline': 'Alle grauen entfernen',
  'session.confirmKickOffline':
    'Alle grauen Namen entfernen ({count})? Diese Handys sind gerade nicht verbunden. Kommt ein Kind zurück, tritt es mit der PIN neu bei.',
  'session.rename': 'Umbenennen',
  'session.renamePrompt': 'Neuer Name',
  'session.renameTitle': '{name} umbenennen',
  'session.gameOver': 'Spiel zu Ende. „Schließen“ beendet es für alle.',
  'session.confirmEnd': 'Das Spiel wird für alle beendet.',
  'session.confirmEndRanking':
    'Spiel beenden? Die Tafel zeigt erst den Endstand. Danach beendet „Schließen“ das Spiel für alle. Lief noch nichts, ist das Spiel gleich vorbei.',
  'session.confirmClose': 'Das Spiel für alle schließen? Die Handys verlassen das Spiel.',
  'session.gone': 'Dieses Spiel gibt es nicht mehr.',
  'session.goneHint':
    'Vielleicht wurde der Server neu gestartet. Starte das Spiel im Lehrerbereich neu. Die Kinder treten dann mit der neuen PIN bei.',
  'session.toTeacher': 'Zum Lehrerbereich',

  'board.fullscreen': 'Vollbild',
  'board.soundOff': 'Für Ton einmal auf die Tafel klicken',
  'board.soundOn': 'Ton ist an',
  'board.loading': 'Medien werden geladen … {done} von {total}',
  'board.noDevices': 'Schaut auf die Tafel, gleich geht es los.',
  'board.waitingForPlayers': 'Wir warten auf die ersten Mitspieler …',
  'board.locked': 'Beitritt gesperrt',
  'board.thanks': 'Danke fürs Mitspielen.',

  'join.title': 'Mitspielen',
  'join.pin': 'PIN von der Tafel',
  'join.name': 'Dein Name',
  'join.submit': 'Los geht’s',
  'join.pinInvalid': 'Die PIN hat sechs Ziffern.',
  'join.nameMissing': 'Der Name braucht mindestens zwei Zeichen.',
  'join.waiting': 'Du bist dabei. Gleich geht es los.',
  'join.lookAtBoard': 'Schau auf die Tafel.',
  'join.replaced':
    'Dieses Spiel ist jetzt in einem anderen Tab oder auf einem anderen Gerät offen.',
  'join.takeBack': 'Hier weiterspielen',
  'join.left': 'Du hast das Spiel verlassen.',
  'join.otherGame': 'Anderes Spiel?',
  'join.otherGameShort': 'Anderes Spiel?',
  'join.leaveConfirm': 'Dieses Spiel verlassen? Dein Platz und deine Punkte sind dann weg.',
  'join.leaveButton': 'Verlassen',
  'join.ended': 'Das Spiel ist vorbei.',
  'join.again': 'Neu beitreten',
  'join.askName': 'Bist du {name}?',
  'join.askYes': 'Ja, ich bin {name}',
  'join.askNo': 'Nein, ich bin neu',

  'editor.newTitle': 'neues Fragenset',
  'editor.editTitle': 'Fragenset bearbeiten',
  'editor.notFound': 'Dieses Fragenset gibt es nicht.',
  'editor.title': 'Titel',
  'editor.titlePlaceholder': 'z. B. Mittelalter, Klasse 7',
  'editor.save': 'Speichern',
  'editor.saving': 'Speichert …',
  'editor.saved': 'Gespeichert',
  'editor.ready': 'Gespeichert, spielbereit',
  'editor.draft': 'Gespeichert als Entwurf: noch {count} offen',
  'editor.openIssues': 'Was fehlt noch? ({count})',
  'editor.applyJson': 'Übernehmen',
  'editor.applied': 'Übernommen.',
  'editor.notSaved': 'Nicht gespeichert',
  'editor.unsaved': 'Ungespeicherte Änderungen',
  'editor.tabVisual': 'Editor',
  'editor.tabJson': 'JSON',
  'editor.jsonLabel': 'Fragenset als JSON',
  'editor.import': 'Datei importieren',
  'editor.export': 'Als Datei exportieren',
  'editor.noVisual':
    'Für dieses Format gibt es noch keinen grafischen Editor. Du kannst das Set hier als JSON bearbeiten.',
  'editor.badJson': 'Das ist kein gültiges JSON.',
  'editor.wrongGame': 'Diese Datei gehört zu einem anderen Spielformat.',
  'editor.imported': 'Importiert. Prüfen und speichern.',

  'media.title': 'Medien',
  'media.lead':
    'Alles, was du in Fragen zeigen oder abspielen willst. Bilder werden automatisch verkleinert.',

  'conn.connecting': 'Verbinde …',
  'conn.reconnecting': 'Verbindung verloren, versuche es erneut …',
  'conn.closed': 'Verbindung beendet.',
  'error.generic': 'Da ist etwas schiefgegangen.',
} as const;

export type MessageKey = keyof typeof de;

/** Minimal interpolation: replaces `{name}` with params[name]; unknown placeholders stay visible. */
export function t(key: MessageKey, params: Record<string, string | number> = {}): string {
  const template: string = de[key];
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? `{${name}}`));
}
